import {useInfiniteQuery, useQueryClient} from '@tanstack/react-query';
import {useState} from 'react';
import {DataTableSortStatus} from 'mantine-datatable';
// The comparator is SHARED (FNP-912): nulls-last and dotted accessors
// ('billingProvider.name') live in the library, not in a per-module copy.
import {useTableSort} from '@flexinet/ui-components';

import {fetchRecords} from '../services/ProviderMappingsService';
import {ProviderMapping} from '../types';
import {useProviderMappingsFilter} from './useProviderMappingsFilter';
import {providerMappingsQueryKeys} from './providerMappingsQueryKeys';

// Page size is a server concern fixed by the module (§9), never a user choice.
export const PAGE_SIZE = 10;

// NEWEST FIRST, on `created`: AWSDateTime! in the API, and the list index's sort key
// (record-type-created-index, ScanIndexForward false), so first paint agrees with the
// server's own order. The billing-api resolver drops the `sort` argument
// (provider-mappings/list.ts passes only filter, limit and nextToken), so ordering is
// applied here rather than requested.
export const DEFAULT_SORT_STATUS: DataTableSortStatus<ProviderMapping> = {
    columnAccessor: 'created',
    direction: 'desc',
};

export type ProviderMappingsContext = {
    productId?: string,
    customerId?: string,
    priceId?: string,
};

// The profile tab a list is embedded in pins it to one local entity.
const contextFilterFor = ({productId, customerId, priceId}: ProviderMappingsContext): Record<string, string> => {
    if (productId) return {entityType: 'Product', localEntityId: productId};
    if (customerId) return {entityType: 'Customer', localEntityId: customerId};
    if (priceId) return {entityType: 'Price', localEntityId: priceId};
    return {};
};

const matchesKeyword = (item: ProviderMapping, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    const haystack = [
        item.entityType,
        item.billingProvider?.name,
        item.billingProviderId,
        item.providerEntityId,
        item.localEntityId,
        item.syncStatus,
        item.id,
    ];
    return haystack.some(value => typeof value === 'string' && value.toLowerCase()
                                                                    .includes(needle));
};

export const useProviderMappings = (context: ProviderMappingsContext = {}) => {
    const queryClient = useQueryClient();
    const {filters} = useProviderMappingsFilter();

    // Component state: the search input (TableSearchForm) and the table that reads the
    // keyword are the SAME component, RecordsTable, so there is no sibling to share with.
    const [keyword, setKeyword] = useState('');

    // The context goes LAST: on a product tab the list must stay that product's mappings
    // even when the drawer's entity-type filter says otherwise.
    const filter = {...filters, ...contextFilterFor(context)};

    // Only what the server filters on is in the key. The keyword is applied client-side
    // below, so keying on it would refetch identical pages per keystroke.
    const query = useInfiniteQuery({
        queryKey: providerMappingsQueryKeys.list(filter),
        queryFn: ({pageParam}: { pageParam: string | null }) => fetchRecords({
            limit: PAGE_SIZE,
            nextToken: pageParam,
            filter,
        }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
        // Mappings change only through this module (create, delete) and the provider sync
        // seam; every module mutation invalidates ['provider-mappings'], so 30s stops a
        // remount refetching without hiding an edit made here.
        staleTime: 30_000,
    });

    const items: ProviderMapping[] =
        query.data?.pages?.flatMap(page => (page && 'items' in page && Array.isArray(page.items))
            ? page.items.filter((item): item is ProviderMapping => !!item)
            : []) ?? [];

    const needle = keyword.trim()
                          .toLowerCase();

    // ⚠️ Client-side filter and sort cover the pages FETCHED SO FAR. Under token
    // pagination the browser cannot order or search rows it has not loaded; that is a
    // property of the model, not a bug (§9). The API also filters AFTER its Limit, so a
    // filtered page can come back short or empty while a nextToken still exists: the
    // footer's Load More is how the operator reaches the rest.
    const {sortStatus, setSortStatus, sorted: visibleItems} =
        useTableSort(items.filter(item => matchesKeyword(item, needle)), DEFAULT_SORT_STATUS);

    const lastPage = query.data?.pages?.[query.data.pages.length - 1];

    return {
        // The loaded set BEFORE keyword filtering: what the footer's "N of M loaded" is
        // measured against.
        items,
        visibleItems,
        keyword,
        // The operator's drawer filters only. The embedded tab's own scope is not a
        // filter the operator can clear, so the empty state must not name it as one.
        filters,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        // Reload and invalidation after a create/delete refetch without isLoading.
        fetching: query.isFetching && !query.isFetchingNextPage,
        isError: query.isError,
        isFetchingMore: query.isFetchingNextPage,
        // The server's own cursor, never a stand-in: opaque to this client (§9).
        nextToken: query.hasNextPage ? (lastPage?.nextToken ?? null) : null,
        // Loads the NEXT page. Guarded on isFetchingNextPage too, so scroll-to-bottom and
        // Load More cannot start overlapping requests for the same cursor.
        fetchMoreRecords: () => {
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        // Reload starts again from page 1, for every list instance on the page.
        reloadRecords: () => queryClient.invalidateQueries({queryKey: providerMappingsQueryKeys.all}),
        handleFilterByKeyword: (newKeyword: string) => setKeyword(newKeyword),
    };
};
